import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import { JwtPayload } from '@interfaces';

/**
 * 기본 Cookie 설정 옵션
 */
const BASE_COOKIE_OPTIONS: Cookies.CookieAttributes = {
  path: '/',
  sameSite: 'strict',
  secure: window.location.protocol === 'https:',
};

/**
 * 기본 만료 기간 (일)
 */
const DEFAULT_EXPIRY_DAYS = 7;

/**
 * JWT 토큰에서 만료 시간(일 수)을 추출
 * js-cookie는 Date 객체보다 일 수(number)가 더 안정적으로 동작
 */
const getTokenExpDays = (token: string): number => {
  try {
    const decoded = jwtDecode<{ exp?: number }>(token);
    if (decoded.exp) {
      const expDate = new Date(decoded.exp * 1000);
      const now = new Date();
      const diffMs = expDate.getTime() - now.getTime();
      const diffDays = diffMs / (1000 * 60 * 60 * 24);
      // 최소 1일, 음수면 기본값 사용
      return diffDays > 0 ? Math.max(diffDays, 1 / 24) : DEFAULT_EXPIRY_DAYS;
    }
  } catch {
    // 디코딩 실패 시 기본값 반환
  }
  return DEFAULT_EXPIRY_DAYS;
};

/**
 * 프로필 인터페이스
 */
export interface Profile {
  name: string;
  id_admin: string;
}

/**
 * 통합 인증 데이터 인터페이스
 */
export interface AuthData {
  token: string | null;
  refreshToken: string | null;
  profile: Profile | null;
  roles: string[];
  decodedToken: JwtPayload | null;
  sessionID: string | null;
  userId: string | null;
}

/**
 * Cookie 키 상수 (인증 토큰용)
 */
const COOKIE_KEYS = {
  TOKEN: 'token',
  REFRESH_TOKEN: 'refreshToken',
  SESSION_ID: 'sessionID',
  USER_ID: 'userId',
} as const;

/**
 * localStorage 키 상수 (비민감 데이터용)
 */
const STORAGE_KEYS = {
  PROFILE: 'profile',
  ROLES: 'roles',
  USERNAME: 'username',
  IS_WAREHOUSE_MASTER: 'isWarehouseMaster',
} as const;

/**
 * 안전한 JSON 파싱 헬퍼
 */
const safeJsonParse = <T>(value: string | null, defaultValue: T): T => {
  if (!value) return defaultValue;

  try {
    const parsed = JSON.parse(value) as T;
    // null이 파싱된 경우 defaultValue 반환
    if (parsed === null) return defaultValue;
    return parsed;
  } catch {
    // 개발 환경에서만 에러 로깅 (프로덕션에서는 조용히 무시)
    if (process.env.NODE_ENV === 'development') {
      console.warn('Auth: JSON 파싱 실패 (무시됨)', { value });
    }
    return defaultValue;
  }
};

/**
 * 안전한 JWT 디코딩 헬퍼
 */
const safeJwtDecode = (token: string | null): JwtPayload | null => {
  if (!token) return null;

  try {
    const decoded = jwtDecode<JwtPayload>(token);

    // 토큰 만료 확인
    if (decoded.exp && decoded.exp * 1000 < Date.now()) {
      // 만료된 토큰은 조용히 null 반환 (정상적인 상황)
      return null;
    }

    return decoded;
  } catch {
    // 유효하지 않은 토큰은 조용히 null 반환 (에러 로깅 제거)
    // 개발 환경에서 디버깅이 필요한 경우에만 주석 해제
    // if (process.env.NODE_ENV === 'development') {
    //   console.warn('Auth: JWT 디코딩 실패 (무시됨)', { token: token.substring(0, 20) + '...' });
    // }
    return null;
  }
};

/**
 * 인증 관련 저장소 추상화 유틸리티
 * - 토큰: Cookie (보안)
 * - 프로필/역할: localStorage (비민감 데이터)
 */
export const AuthStorage = {
  // ============================================
  // Cookie 저장 (인증 토큰 - 보안 필요)
  // ============================================

  /**
   * 액세스 토큰 가져오기
   */
  getToken(): string | null {
    return Cookies.get(COOKIE_KEYS.TOKEN) ?? null;
  },

  /**
   * 액세스 토큰 저장 (refreshToken의 만료 시간에 맞춤)
   */
  setToken(token: string, refreshTokenForExpiry?: string): void {
    const refreshToken = refreshTokenForExpiry ?? this.getRefreshToken();
    const expires = refreshToken ? getTokenExpDays(refreshToken) : DEFAULT_EXPIRY_DAYS;
    Cookies.set(COOKIE_KEYS.TOKEN, token, { ...BASE_COOKIE_OPTIONS, expires });
  },

  /**
   * 리프레시 토큰 가져오기
   */
  getRefreshToken(): string | null {
    return Cookies.get(COOKIE_KEYS.REFRESH_TOKEN) ?? null;
  },

  /**
   * 리프레시 토큰 저장 (토큰의 만료 시간에 맞춤)
   */
  setRefreshToken(token: string): void {
    const expires = getTokenExpDays(token);
    Cookies.set(COOKIE_KEYS.REFRESH_TOKEN, token, { ...BASE_COOKIE_OPTIONS, expires });
  },

  /**
   * 세션 ID 가져오기
   */
  getSessionID(): string | null {
    return Cookies.get(COOKIE_KEYS.SESSION_ID) ?? null;
  },

  /**
   * 세션 ID 저장 (refreshToken의 만료 시간에 맞춤)
   */
  setSessionID(sessionID: string): void {
    const refreshToken = this.getRefreshToken();
    const expires = refreshToken ? getTokenExpDays(refreshToken) : DEFAULT_EXPIRY_DAYS;
    Cookies.set(COOKIE_KEYS.SESSION_ID, sessionID, { ...BASE_COOKIE_OPTIONS, expires });
  },

  /**
   * 사용자 ID 가져오기
   */
  getUserId(): string | null {
    return Cookies.get(COOKIE_KEYS.USER_ID) ?? null;
  },

  /**
   * 사용자 ID 저장 (refreshToken의 만료 시간에 맞춤)
   */
  setUserId(userId: string): void {
    const refreshToken = this.getRefreshToken();
    const expires = refreshToken ? getTokenExpDays(refreshToken) : DEFAULT_EXPIRY_DAYS;
    Cookies.set(COOKIE_KEYS.USER_ID, userId, { ...BASE_COOKIE_OPTIONS, expires });
  },

  // ============================================
  // localStorage 저장 (비민감 데이터)
  // ============================================

  /**
   * 프로필 정보 가져오기 (안전한 파싱)
   */
  getProfile(): Profile | null {
    const profileStr = localStorage.getItem(STORAGE_KEYS.PROFILE);
    return safeJsonParse<Profile | null>(profileStr, null);
  },

  /**
   * 프로필 정보 저장
   */
  setProfile(profile: Profile): void {
    localStorage.setItem(STORAGE_KEYS.PROFILE, JSON.stringify(profile));
  },

  /**
   * 역할(roles) 배열 가져오기 (안전한 파싱)
   */
  getRoles(): string[] {
    const rolesStr = localStorage.getItem(STORAGE_KEYS.ROLES);
    return safeJsonParse<string[]>(rolesStr, []);
  },

  /**
   * 역할(roles) 배열 저장
   */
  setRoles(roles: string[]): void {
    localStorage.setItem(STORAGE_KEYS.ROLES, JSON.stringify(roles));
  },

  /**
   * 로그인 상태 유지용 username 저장
   */
  setRememberedUsername(username: string): void {
    localStorage.setItem(STORAGE_KEYS.USERNAME, username);
  },

  /**
   * 저장된 username 가져오기
   */
  getRememberedUsername(): string | null {
    return localStorage.getItem(STORAGE_KEYS.USERNAME);
  },

  /**
   * 저장된 username 삭제
   */
  clearRememberedUsername(): void {
    localStorage.removeItem(STORAGE_KEYS.USERNAME);
  },

  // ============================================
  // 헬퍼 메서드
  // ============================================

  /**
   * 디코딩된 JWT 토큰 정보 가져오기 (안전한 디코딩)
   */
  getDecodedToken(): JwtPayload | null {
    const token = this.getToken();
    return safeJwtDecode(token);
  },

  /**
   * 특정 역할 보유 여부 확인
   */
  hasRole(role: string): boolean {
    const roles = this.getRoles();
    return roles.includes(role);
  },

  /**
   * 여러 역할 중 하나라도 보유 여부 확인
   */
  hasAnyRole(requiredRoles: string[]): boolean {
    const roles = this.getRoles();
    return requiredRoles.some(role => roles.includes(role));
  },

  /**
   * 모든 역할 보유 여부 확인
   */
  hasAllRoles(requiredRoles: string[]): boolean {
    const roles = this.getRoles();
    return requiredRoles.every(role => roles.includes(role));
  },

  /**
   * 통합 인증 데이터 가져오기
   */
  getAuthData(): AuthData {
    const token = this.getToken();
    const decodedToken = this.getDecodedToken();

    return {
      token,
      refreshToken: this.getRefreshToken(),
      profile: this.getProfile(),
      roles: this.getRoles(),
      decodedToken,
      sessionID: this.getSessionID(),
      userId: this.getUserId(),
    };
  },

  /**
   * 인증 여부 확인
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    const decodedToken = this.getDecodedToken();
    return Boolean(token && decodedToken);
  },

  /**
   * 모든 인증 데이터 삭제 (로그아웃)
   */
  clearAuth(): void {
    // Cookie 삭제
    Cookies.remove(COOKIE_KEYS.TOKEN, { path: '/' });
    Cookies.remove(COOKIE_KEYS.REFRESH_TOKEN, { path: '/' });
    Cookies.remove(COOKIE_KEYS.SESSION_ID, { path: '/' });
    Cookies.remove(COOKIE_KEYS.USER_ID, { path: '/' });
    // localStorage 삭제
    localStorage.removeItem(STORAGE_KEYS.PROFILE);
    localStorage.removeItem(STORAGE_KEYS.ROLES);
    localStorage.removeItem(STORAGE_KEYS.IS_WAREHOUSE_MASTER);
  },
};
